import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { SeoMetadataService } from '@uc/utilities';
import { ENGAGE_DATA, META_DATA } from '../../static/home.properties';
import {
	CourseSearchPathService,
	HECoSSubjectValidatorService,
	JsonLdSchemaService,
} from '@uc/web/core';
import {
	CourseSearchBarComponent,
	DownloadAppCtaComponent,
	EngageCardCtaComponent,
	EngageCardCtaDegreeQuizComponent,
	MainHeaderComponent,
	StaticCardComponent,
} from '@web/shared/ui';
import { ClearingSearchComponent } from '@uc/web/shared/clearing-ui';
import { Router, RouterLink } from '@angular/router';
import {
	CourseSearchLocationGAEnum,
	DegreeLevelOption,
} from '@uc/web/shared/data-models';
import { UsersSolidSvgComponent } from '@uc/shared/svg';
import { FeaturedUniversityComponent } from '@uc/web/shared/features/feature-featured-university';
import { PrimeProvidersComponent } from '@uc/web/shared/features/feature-prime-providers';
import { PromoSlotsComponent } from '@uc/web/shared/features/feature-promo-slots';
import { PopularSubjectsComponent } from '@uc/web/shared/features/feature-popular-subjects';
import {
	BASIC_DEGREE_LEVEL_OPTIONS,
	CLEARING_DEGREE_LEVEL_OPTION,
	DegreeLevelService,
} from '@uc/web/shared/data-access';
import { ClearingArticlesCarouselComponent } from '@uc/web/shared/features/feature-clearing-articles-carousel';
import { RecommendedUnisGridComponent } from '@uc/web/shared/features/feature-recommended-universities';
import { CURRENT_RANKINGS_YEAR } from '../../../rankings/static/rankings.properties';
import { CLEARING_ENABLED } from '@uc/web/config';
import { DegreeQuizGAService } from '@uc/web/analytics';

@Component({
	selector: 'uc-home',
	templateUrl: './home.component.html',
	imports: [
		RouterLink,
		MainHeaderComponent,
		UsersSolidSvgComponent,
		EngageCardCtaComponent,
		PromoSlotsComponent,
		PopularSubjectsComponent,
		DownloadAppCtaComponent,
		EngageCardCtaDegreeQuizComponent,
		ClearingSearchComponent,
		StaticCardComponent,
		CourseSearchBarComponent,
		PrimeProvidersComponent,
		FeaturedUniversityComponent,
		RecommendedUnisGridComponent,
		ClearingArticlesCarouselComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent implements OnInit, OnDestroy {
	ctaContent = ENGAGE_DATA;
	currentYear = new Date().getFullYear();
	currentRankingsYear = CURRENT_RANKINGS_YEAR;
	clearingEnabled = CLEARING_ENABLED;
	degreeLevelOptions: DegreeLevelOption[] = [...BASIC_DEGREE_LEVEL_OPTIONS];

	readonly headerSearchBar = CourseSearchLocationGAEnum.HeaderSearchBar;

	constructor(
		private _router: Router,
		private _seoSrv: SeoMetadataService,
		private _jsonLdSrv: JsonLdSchemaService,
		private _degreeQuizGASrv: DegreeQuizGAService,
		private _degreeLevelSrv: DegreeLevelService,
		private _hecosSubjectValidatorSrv: HECoSSubjectValidatorService,
		private _courseSearchPathSrv: CourseSearchPathService,
	) {}

	ngOnInit(): void {
		this._seoSrv.updateTitle(META_DATA.title);
		this._seoSrv.updateDescription(META_DATA.description);
		this._jsonLdSrv.addHomePageSchema();

		if (this.clearingEnabled) {
			this.degreeLevelOptions = [
				CLEARING_DEGREE_LEVEL_OPTION,
				...this.degreeLevelOptions,
			];
		}

		this._degreeLevelSrv.setDegreeLevels(this.degreeLevelOptions);
		this._degreeLevelSrv.setSelectedDegreeLevel(this.degreeLevelOptions[0]);
		this._degreeLevelSrv.selectedClearingDegreeLevel.set({
			...CLEARING_DEGREE_LEVEL_OPTION,
		});
	}

	ngOnDestroy(): void {
		this._jsonLdSrv.removeSchema('home-page-schema');
	}

	async onNavigate(autosuggest: { searchTerm: string; degree?: string }) {
		if (!autosuggest.searchTerm) return;

		const { searchTerm, degree } = autosuggest;

		const subject = searchTerm.replace(/ /g, '-').toLowerCase();
		const isHECoSSubject =
			await this._hecosSubjectValidatorSrv.isHECoSSubject(subject);

		const segments = this._courseSearchPathSrv.getCourseSearchSegmentsWithoutLocation(
			isHECoSSubject,
			subject,
			degree,
		);

		this._router.navigate([...segments], {
			queryParams: isHECoSSubject ? {} : { search: subject },
		});
	}

	onQuizButtonClick = () => this._degreeQuizGASrv.onQuizButtonClick('default');

	getTitle = () => {
		return this.clearingEnabled
			? 'Search Clearing Courses at UK Universities'
			: 'Start your university journey right here';
	};

	getDescription = () => {
		return this.clearingEnabled
			? `Search and compare Clearing ${this.currentYear} courses.`
			: 'Search for universities, compare courses and read real reviews.';
	};
}
